import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy,
  Zap,
  BarChart3,
  QrCode,
  Mail,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const FEATURES = [
  {
    id: 0,
    eyebrow: "FRICTIONLESS ONBOARDING",
    title: "One email. You're in.",
    desc: "No phone OTP gymnastics, no password resets. Drop your email, grab a sign-in code, and you're already adding your first expense.",
    icon: Mail,
    image:
      "https://customer-assets.emergentagent.com/job_app-showcase-256/artifacts/fsrxe1u5_WhatsApp%20Image%202026-05-02%20at%2001.34.56.jpeg",
    tone: "#3056F5",
  },
  {
    id: 1,
    eyebrow: "GROUP ROLES",
    title: "Every member earns a vibe.",
    desc: "The ATM. The Borrower. The Equalizer. Qontri assigns playful roles based on real spending and settling behaviour — banter included.",
    icon: Trophy,
    image:
      "https://customer-assets.emergentagent.com/job_25687ba4-de6b-41cb-ba59-a8223df26ff0/artifacts/dv3fu4en_WhatsApp%20Image%202026-05-02%20at%2001.34.51%20%281%29.jpeg",
    tone: "#0F2C5A",
  },
  {
    id: 2,
    eyebrow: "SIMPLIFIED SETTLEMENTS",
    title: "Fewer transfers. Zero confusion.",
    desc: "One tap to switch from full ledger to a simplified plan. Qontri figures out the minimum number of payments to settle everyone up.",
    icon: Zap,
    image:
      "https://customer-assets.emergentagent.com/job_25687ba4-de6b-41cb-ba59-a8223df26ff0/artifacts/xuqj8dc2_WhatsApp%20Image%202026-05-02%20at%2001.34.54%20%283%29.jpeg",
    tone: "#3056F5",
  },
  {
    id: 3,
    eyebrow: "INSIGHTS & PROFILE",
    title: "See how your crew really spends.",
    desc: "Groups, expenses, your share — all glance-able. A clean profile that doubles as your trip ledger across every adventure.",
    icon: BarChart3,
    image:
      "https://customer-assets.emergentagent.com/job_25687ba4-de6b-41cb-ba59-a8223df26ff0/artifacts/xvl7fc0p_WhatsApp%20Image%202026-05-02%20at%2001.34.55%20%282%29.jpeg",
    tone: "#0F2C5A",
  },
  {
    id: 4,
    eyebrow: "BRANDED UPI QR",
    title: "Pay friends like you pay merchants.",
    desc: "Add your UPI ID once. Qontri generates a branded QR. GPay, PhonePe, Paytm — scan and settle in seconds, no app-juggling.",
    icon: QrCode,
    image:
      "https://customer-assets.emergentagent.com/job_app-showcase-256/artifacts/iqad66ba_WhatsApp%20Image%202026-05-09%20at%2019.23.19%20%281%29.jpeg",
    tone: "#3056F5",
  },
];

const AUTOPLAY_MS = 5200;

export default function Features() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dir, setDir] = useState(1);

  const next = useCallback(() => {
    setDir(1);
    setIdx((i) => (i + 1) % FEATURES.length);
  }, []);
  const prev = useCallback(() => {
    setDir(-1);
    setIdx((i) => (i - 1 + FEATURES.length) % FEATURES.length);
  }, []);
  const goTo = useCallback((i) => {
    setDir(i > idx ? 1 : -1);
    setIdx(i);
  }, [idx]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [next, paused]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const active = FEATURES[idx];
  const Icon = active.icon;

  return (
    <section
      id="features"
      data-testid="features-carousel-section"
      className="relative bg-transparent py-20 md:py-28 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-16">
          <div>
            <p
              data-testid="features-eyebrow"
              className="text-[#3056F5] font-bold tracking-[0.35em] text-xs mb-4"
            >
              FEATURES
            </p>
            <h2 className="font-display uppercase text-[#0F2C5A] leading-[0.95] text-[clamp(2rem,5vw,4rem)]">
              Built for the way
              <br />
              <span className="text-[#3056F5]">your crew actually moves.</span>
            </h2>
          </div>

          {/* Arrows */}
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              data-testid="features-prev"
              aria-label="Previous feature"
              className="w-12 h-12 rounded-full border border-[#0F2C5A]/15 bg-[#F0EAFD] hover:bg-[#0F2C5A] hover:text-[#F0EAFD] text-[#0F2C5A] flex items-center justify-center transition-all hover:-translate-y-0.5"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              data-testid="features-next"
              aria-label="Next feature"
              className="w-12 h-12 rounded-full bg-[#0F2C5A] hover:bg-[#3056F5] text-[#F0EAFD] flex items-center justify-center transition-all hover:-translate-y-0.5"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Carousel body */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* Phone */}
          <div className="md:col-span-5 flex justify-center md:justify-start">
            <div className="relative w-[230px] md:w-[300px] aspect-[9/19]">
              <div className="absolute inset-0 rounded-[2.6rem] p-1.5 bg-[#0F2C5A] shadow-2xl shadow-[#0F2C5A]/25">
                <div className="relative overflow-hidden rounded-[2.1rem] w-full h-full bg-white">
                  <AnimatePresence mode="wait" custom={dir}>
                    <motion.img
                      key={active.id}
                      src={active.image}
                      alt={active.title}
                      custom={dir}
                      initial={{ opacity: 0, x: dir * 40, scale: 1.04 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: -dir * 40, scale: 0.98 }}
                      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      data-testid={`feature-image-${idx}`}
                    />
                  </AnimatePresence>
                </div>
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#0F2C5A] rounded-full" />
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="md:col-span-7 relative min-h-[280px] md:min-h-[340px]">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={active.id}
                custom={dir}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`feature-block-${idx}`}
              >
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F2C5A]/5 border border-[#0F2C5A]/10 mb-5"
                  style={{ color: active.tone }}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-[11px] font-bold tracking-[0.2em]">
                    {active.eyebrow}
                  </span>
                </div>
                <h3 className="font-display uppercase text-[#0F2C5A] leading-[0.95] text-[clamp(2rem,4.5vw,3.6rem)]">
                  {active.title}
                </h3>
                <p className="mt-6 max-w-xl text-base md:text-lg text-[#14254B]/80 leading-relaxed">
                  {active.desc}
                </p>
                <div className="mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.25em] font-bold text-[#3056F5]">
                  <span className="block w-10 h-px bg-[#3056F5]" />
                  {String(idx + 1).padStart(2, "0")} /{" "}
                  {String(FEATURES.length).padStart(2, "0")}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Dots / Tabs */}
        <div
          data-testid="features-dots"
          className="mt-12 md:mt-16 flex flex-wrap items-center gap-3 justify-center md:justify-start"
        >
          {FEATURES.map((f, i) => {
            const active = i === idx;
            return (
              <button
                key={f.id}
                onClick={() => goTo(i)}
                data-testid={`features-dot-${i}`}
                aria-label={`Go to feature ${i + 1}`}
                className={`group relative overflow-hidden rounded-full transition-all border ${
                  active
                    ? "bg-[#0F2C5A] text-[#F0EAFD] border-[#0F2C5A] px-5 py-2.5"
                    : "bg-transparent text-[#0F2C5A] border-[#0F2C5A]/20 hover:border-[#0F2C5A]/50 px-4 py-2.5"
                }`}
              >
                <span className="text-[10px] font-bold tracking-[0.2em]">
                  {String(i + 1).padStart(2, "0")} · {f.eyebrow}
                </span>
                {active && !paused && (
                  <motion.span
                    key={idx}
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{
                      duration: AUTOPLAY_MS / 1000,
                      ease: "linear",
                    }}
                    className="absolute left-0 bottom-0 h-[2px] bg-[#3056F5]"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
